// quote-request.js
// Guided consultation flow: pick specialty + location, then submit consultation request.

async function jsonFetch(url, options = {}) {
  const token = localStorage.getItem('lnf_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(url, {
    ...options,
    headers: { ...headers, ...(options.headers || {}) }
  });
  if (!response.ok) {
    const text = await response.text();
    let message = text || `Request failed: ${response.status}`;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) message = parsed.error;
    } catch (_err) {
      // keep raw text fallback
    }
    throw new Error(message);
  }
  return response.status === 204 ? null : response.json();
}

document.addEventListener('DOMContentLoaded', () => {
  const quoteSelectorPanel = document.getElementById('quoteSelectorPanel');
  const quoteSpecialtyGrid = document.getElementById('quoteSpecialtyGrid');
  const quoteSelectorStatus = document.getElementById('quoteSelectorStatus');
  const quoteConsultationCard = document.getElementById('quoteConsultationCard');
  const quoteMatchSummary = document.getElementById('quoteMatchSummary');
  const changeQuoteSelectionBtn = document.getElementById('changeQuoteSelectionBtn');
  const quoteLocationModal = document.getElementById('quoteLocationModal');
  const closeQuoteLocationModal = document.getElementById('closeQuoteLocationModal');
  const cancelQuoteLocationBtn = document.getElementById('cancelQuoteLocationBtn');
  const quoteLocationSelect = document.getElementById('quoteLocationSelect');
  const quoteUseCurrentLocationBtn = document.getElementById('quoteUseCurrentLocationBtn');
  const quoteConfirmLocationBtn = document.getElementById('quoteConfirmLocationBtn');
  const quoteLocationStatus = document.getElementById('quoteLocationStatus');

  const quoteForm = document.getElementById('quoteForm');
  const quoteNodeInput = document.getElementById('quoteNode');
  const selectedServiceCategoryInput = document.getElementById('selectedServiceCategory');
  const selectedServiceOptionInput = document.getElementById('selectedServiceOption');
  const selectedRequestCountyInput = document.getElementById('selectedRequestCounty');
  const selectedRequestStateInput = document.getElementById('selectedRequestState');
  const selectedNeedLabelInput = document.getElementById('selectedNeedLabel');

  const full_name_input = document.getElementById('full_name');
  const addressInput = document.getElementById('address');
  const contact_pref_input = document.getElementById('contact_pref');
  const phoneField = document.getElementById('phoneField');
  const phoneInput = document.getElementById('phone');
  const emailField = document.getElementById('emailField');
  const emailInput = document.getElementById('email');
  const landGoalsInput = document.getElementById('landGoals');
  const quoteReferralSourceInput = document.getElementById('quoteReferralSource');
  const quoteReferralMemberField = document.getElementById('quoteReferralMemberField');
  const quoteReferralMemberNameInput = document.getElementById('quoteReferralMemberName');
  const preferredMeetingDateInput = document.getElementById('preferredMeetingDate');
  const preferredMeetingTimeInput = document.getElementById('preferredMeetingTime');
  const alternateMeetingDateInput = document.getElementById('alternateMeetingDate');
  const alternateMeetingTimeInput = document.getElementById('alternateMeetingTime');
  const meetingScheduleNotesInput = document.getElementById('meetingScheduleNotes');
  const statusEl = document.getElementById('quoteStatus');

  if (!quoteSpecialtyGrid || !quoteForm || !quoteLocationSelect || !quoteConsultationCard) return;

  const nodeSpecializations = window.LNFNodeSpecializations || {};
  const installCatalog = nodeSpecializations.SPECIALIZATION_CATALOG?.install || [];
  const nodeSupportsMode = typeof nodeSpecializations.nodeSupportsMode === 'function'
    ? nodeSpecializations.nodeSupportsMode
    : (node, mode) => {
        const type = String(node?.node_type || 'hybrid').trim().toLowerCase();
        if (mode === 'install') return type === 'install' || type === 'hybrid';
        if (mode === 'stock') return type === 'stock' || type === 'hybrid';
        return false;
      };
  const normalizeNodeSpecialties = typeof nodeSpecializations.normalizeNodeSpecialties === 'function'
    ? nodeSpecializations.normalizeNodeSpecialties
    : (raw) => {
        if (!Array.isArray(raw)) return [];
        return raw
          .map((entry) => {
            const category = String(entry?.category || '').trim();
            const options = Array.isArray(entry?.options)
              ? entry.options.map((label) => String(label || '').trim()).filter(Boolean)
              : [];
            if (!category || !options.length) return null;
            return { category, options };
          })
          .filter(Boolean);
      };

  const toSlug = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const normalizeKey = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const CATEGORY_DISPLAY_ORDER = {
    install: [
      'Full Property Design',
      'Maintenance',
      'Trees',
      'Ecosystem Restoration',
      'Soil Building',
      'Water Systems'
    ]
  };
  const buildCatalogWithConfiguredSpecialties = (baseCatalog, nodes, mode) => {
    const categoryMap = new Map();
    const baseOrder = [];

    const ensureCategory = (categoryLabel, isBase = false) => {
      const finalCategory = String(categoryLabel || '').trim() || 'Custom';
      const key = normalizeKey(finalCategory);
      if (!categoryMap.has(key)) {
        categoryMap.set(key, {
          category: finalCategory,
          options: [],
          optionSet: new Set()
        });
      }
      const row = categoryMap.get(key);
      if (isBase && !baseOrder.includes(key)) baseOrder.push(key);
      return row;
    };

    const addOption = (row, optionLabel) => {
      const label = String(optionLabel || '').trim();
      if (!label) return;
      const key = normalizeKey(label);
      if (row.optionSet.has(key)) return;
      row.optionSet.add(key);
      row.options.push(label);
    };

    (Array.isArray(baseCatalog) ? baseCatalog : []).forEach((group) => {
      const row = ensureCategory(group?.category || '', true);
      (group?.options || []).forEach((label) => addOption(row, label));
    });

    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      const raw = mode === 'install'
        ? (node?.install_specialties || [])
        : (node?.stock_specialties || []);
      const specialties = normalizeNodeSpecialties(raw, mode);
      specialties.forEach((group) => {
        const row = ensureCategory(group?.category || '', false);
        (group?.options || []).forEach((label) => addOption(row, label));
      });
    });

    const used = new Set();
    const out = [];
    baseOrder.forEach((key) => {
      const row = categoryMap.get(key);
      if (!row || !row.options.length) return;
      out.push({ category: row.category, options: row.options.slice() });
      used.add(key);
    });

    const extras = Array.from(categoryMap.entries())
      .filter(([key, row]) => !used.has(key) && row.options.length)
      .map(([, row]) => ({ category: row.category, options: row.options.slice() }))
      .sort((a, b) => a.category.localeCompare(b.category));

    const merged = out.concat(extras);
    const desiredOrder = CATEGORY_DISPLAY_ORDER[mode] || [];
    if (!desiredOrder.length) return merged;
    const orderIndex = new Map(desiredOrder.map((category, idx) => [normalizeKey(category), idx]));
    return merged.sort((a, b) => {
      const aIndex = orderIndex.has(normalizeKey(a?.category || ''))
        ? orderIndex.get(normalizeKey(a?.category || ''))
        : Number.MAX_SAFE_INTEGER;
      const bIndex = orderIndex.has(normalizeKey(b?.category || ''))
        ? orderIndex.get(normalizeKey(b?.category || ''))
        : Number.MAX_SAFE_INTEGER;
      if (aIndex !== bIndex) return aIndex - bIndex;
      if (aIndex !== Number.MAX_SAFE_INTEGER) return 0;
      return String(a?.category || '').localeCompare(String(b?.category || ''));
    });
  };

  let displayInstallCatalog = buildCatalogWithConfiguredSpecialties(installCatalog, [], 'install');
  let specialtyById = new Map();
  const locationAreaByKey = new Map();
  const bookedSlotsByDate = {};

  const SCHEDULE_START_HOUR = 8;
  const SCHEDULE_END_HOUR = 18;
  const SCHEDULE_DAYS_AHEAD = 60;
  const DEFAULT_TIME_PLACEHOLDER = 'Select a time';
  const timeSlots = Array.from(
    { length: SCHEDULE_END_HOUR - SCHEDULE_START_HOUR + 1 },
    (_, idx) => `${String(SCHEDULE_START_HOUR + idx).padStart(2, '0')}:00`
  );

  let nodesCache = [];
  let locationAreas = [];
  let pendingSpecialty = null;
  let selectedSpecialty = null;
  let selectedArea = null;
  let matchedNode = null;
  const REFERRAL_SOURCE_EXISTING_MEMBER = 'existing-member';

  const normalizeText = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const showSignInGate = () => window.lnfAuthGate?.show?.();
  const setSignInStatus = () => {
    if (!statusEl) return;
    statusEl.textContent = 'Please sign in to request a consultation. ';
    const profileLink = document.createElement('a');
    profileLink.href = 'profile.html';
    profileLink.textContent = 'Go to Profile';
    statusEl.appendChild(profileLink);
  };

  const parseNodeCounty = (node) => {
    return String(
      node?.mailing_county
      || node?.county
      || node?.mailing_city
      || node?.city
      || ''
    ).trim();
  };

  const parseNodeState = (node) => {
    return String(node?.mailing_state || node?.state || '').trim();
  };

  const formatNodeLabel = (node) => {
    const name = node?.name || node?.label || 'Node';
    const city = node?.mailing_city || node?.city || '';
    const state = parseNodeState(node);
    const cityState = [city, state].filter(Boolean).join(', ');
    return cityState ? `${name} (${cityState})` : name;
  };

  const toAreaKey = (county, state) => `${normalizeText(county)}::${normalizeText(state)}`;

  const buildLocationAreas = (nodes) => {
    const grouped = new Map();
    nodes.forEach((node) => {
      const county = parseNodeCounty(node);
      const state = parseNodeState(node);
      if (!county || !state) return;
      const key = toAreaKey(county, state);
      if (!grouped.has(key)) {
        grouped.set(key, { key, county, state, nodes: [] });
      }
      grouped.get(key).nodes.push(node);
    });
    return Array.from(grouped.values()).sort((a, b) => {
      if (a.state !== b.state) return a.state.localeCompare(b.state);
      return a.county.localeCompare(b.county);
    });
  };

  const renderLocationOptions = () => {
    locationAreaByKey.clear();
    quoteLocationSelect.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = locationAreas.length ? 'Select county, state' : 'No county/state locations available';
    placeholder.selected = true;
    quoteLocationSelect.appendChild(placeholder);
    locationAreas.forEach((area) => {
      const option = document.createElement('option');
      option.value = area.key;
      option.textContent = `${area.county}, ${area.state}`;
      quoteLocationSelect.appendChild(option);
      locationAreaByKey.set(area.key, area);
    });
    quoteConfirmLocationBtn.disabled = !locationAreas.length;
  };

  const renderSpecialtyGrid = () => {
    quoteSpecialtyGrid.innerHTML = '';
    specialtyById = new Map();
    displayInstallCatalog.forEach((category, categoryIdx) => {
      const categoryLabel = String(category?.category || '').trim();
      if (!categoryLabel) return;
      const card = document.createElement('article');
      card.className = 'specialty-group-card';
      const heading = document.createElement('h3');
      heading.textContent = categoryLabel;
      card.appendChild(heading);

      const optionsWrap = document.createElement('div');
      optionsWrap.className = 'specialty-group-card__options';
      (category.options || []).forEach((rawLabel, optionIdx) => {
        const label = String(rawLabel || '').trim();
        if (!label) return;
        const specialty = {
          id: `install-${categoryIdx}-${optionIdx}-${toSlug(categoryLabel)}-${toSlug(label)}`,
          mode: 'install',
          category: categoryLabel,
          label
        };
        specialtyById.set(specialty.id, specialty);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'specialty-choice-button';
        btn.dataset.optionId = specialty.id;
        btn.innerHTML = `<span>${label}</span>`;
        optionsWrap.appendChild(btn);
      });
      card.appendChild(optionsWrap);
      quoteSpecialtyGrid.appendChild(card);
    });
  };

  const openLocationModal = (specialty) => {
    pendingSpecialty = specialty;
    quoteLocationStatus.textContent = '';
    quoteLocationSelect.value = '';
    quoteLocationModal.classList.remove('is-hidden');
  };

  const closeLocationModal = () => {
    quoteLocationModal.classList.add('is-hidden');
  };

  const showConsultationForm = () => {
    quoteSelectorPanel.classList.add('is-hidden');
    quoteConsultationCard.classList.remove('is-hidden');
  };

  const showSelectorPanel = () => {
    quoteConsultationCard.classList.add('is-hidden');
    quoteSelectorPanel.classList.remove('is-hidden');
    quoteSelectorStatus.textContent = '';
  };

  const applyMatchedContext = () => {
    if (!matchedNode || !selectedSpecialty) return;
    if (quoteNodeInput) quoteNodeInput.value = String(matchedNode.id || '');
    if (selectedServiceCategoryInput) selectedServiceCategoryInput.value = selectedSpecialty.category || '';
    if (selectedServiceOptionInput) selectedServiceOptionInput.value = selectedSpecialty.label || '';
    if (selectedRequestCountyInput) selectedRequestCountyInput.value = selectedArea?.county || '';
    if (selectedRequestStateInput) selectedRequestStateInput.value = selectedArea?.state || '';
    if (selectedNeedLabelInput) {
      selectedNeedLabelInput.value = `${selectedSpecialty.category}: ${selectedSpecialty.label}`;
    }

    const areaLabel = selectedArea
      ? `${selectedArea.county}${selectedArea.state ? `, ${selectedArea.state}` : ''}`
      : (selectedArea?.label || 'your location');
    quoteMatchSummary.textContent = `Matched with ${formatNodeLabel(matchedNode)} for ${selectedSpecialty.label} in ${areaLabel}.`;
  };

  const parseApiError = (err) => {
    const message = String(err?.message || '').trim();
    if (!message) return 'Unable to match a node right now. Please try again.';
    return message;
  };

  const requestNodeMatch = async ({ county = '', state = '', lat = null, lng = null, label = '' } = {}) => {
    if (!pendingSpecialty) return;
    quoteLocationStatus.textContent = 'Finding the best matching node...';
    const params = new URLSearchParams({
      mode: 'install',
      category: pendingSpecialty.category,
      option: pendingSpecialty.label
    });
    if (county && state) {
      params.set('county', county);
      params.set('state', state);
    }
    if (lat != null && lng != null) {
      params.set('lat', String(lat));
      params.set('lng', String(lng));
      params.set('max_miles', '50');
    }

    try {
      const result = await jsonFetch(`/api/nodes/match?${params.toString()}`);
      matchedNode = result?.node || null;
      if (!matchedNode) throw new Error('No matching node returned.');
      selectedSpecialty = pendingSpecialty;
      selectedArea = county && state
        ? { county, state }
        : { county: label || 'Current Location', state: '' };
      applyMatchedContext();
      showConsultationForm();
      closeLocationModal();
      await loadScheduleAvailability();
      quoteLocationStatus.textContent = '';
      statusEl.textContent = '';
      quoteConsultationCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      quoteLocationStatus.textContent = parseApiError(err);
    }
  };

  const loadInstallNodes = async () => {
    quoteSelectorStatus.textContent = 'Loading node locations...';
    try {
      const nodes = await jsonFetch('/api/nodes');
      nodesCache = (Array.isArray(nodes) ? nodes : []).filter((node) => nodeSupportsMode(node, 'install'));
    } catch (err) {
      console.error('load install nodes error:', err);
      nodesCache = [];
    }

    displayInstallCatalog = buildCatalogWithConfiguredSpecialties(installCatalog, nodesCache, 'install');
    renderSpecialtyGrid();

    if (!nodesCache.length) {
      quoteSelectorStatus.textContent = 'No Install Nodes are available yet.';
      locationAreas = [];
      renderLocationOptions();
      return;
    }

    locationAreas = buildLocationAreas(nodesCache);
    renderLocationOptions();
    quoteSelectorStatus.textContent = '';
  };

  const updateContactMethod = () => {
    const pref = (contact_pref_input?.value || '').toLowerCase();
    const needsPhone = pref === 'phone #' || pref === 'phone' || pref === 'phone number';
    const needsEmail = pref === 'email' || pref === 'e-mail';
    phoneField?.classList.toggle('is-hidden', !needsPhone);
    emailField?.classList.toggle('is-hidden', !needsEmail);
    if (!needsPhone && phoneInput) phoneInput.value = '';
    if (!needsEmail && emailInput) emailInput.value = '';
  };

  const replaceOptions = (selectEl, options) => {
    if (!selectEl) return;
    selectEl.innerHTML = '';
    options.forEach((optionDef) => {
      const option = document.createElement('option');
      option.value = optionDef.value;
      option.textContent = optionDef.label;
      if (optionDef.disabled) option.disabled = true;
      if (optionDef.selected) option.selected = true;
      selectEl.appendChild(option);
    });
  };

  const formatHourLabel = (hour) => {
    if (hour === 0) return '12:00 AM';
    if (hour < 12) return `${hour}:00 AM`;
    if (hour === 12) return '12:00 PM';
    return `${hour - 12}:00 PM`;
  };

  const normalizeSlot = (value) => {
    const raw = String(value || '').trim();
    const match = raw.match(/^(\d{1,2})(?::(\d{2}))?$/);
    if (!match) return raw;
    const hour = Number(match[1]);
    const minute = match[2] || '00';
    if (!Number.isFinite(hour)) return raw;
    return `${String(hour).padStart(2, '0')}:${minute}`;
  };

  const isoDateFromOffset = (offset) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + offset);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateLabel = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const populateDateDropdown = (selectEl, { required }) => {
    if (!selectEl) return;
    const previous = selectEl.value;
    const options = required
      ? [{ value: '', label: 'Select a date', disabled: true, selected: true }]
      : [{ value: '', label: 'No alternate date', selected: true }];
    for (let idx = 0; idx < SCHEDULE_DAYS_AHEAD; idx += 1) {
      const iso = isoDateFromOffset(idx);
      options.push({ value: iso, label: formatDateLabel(iso) });
    }
    replaceOptions(selectEl, options);
    if (previous) selectEl.value = previous;
  };

  const getBookedSetForDate = (isoDate) => bookedSlotsByDate[isoDate] || new Set();

  const populateTimeDropdown = (selectEl, { dateValue, required, blockedBySelection = new Set() }) => {
    if (!selectEl) return;
    const previous = selectEl.value;
    const bookedSet = dateValue ? getBookedSetForDate(dateValue) : new Set();
    const options = required
      ? [{ value: '', label: DEFAULT_TIME_PLACEHOLDER, disabled: true, selected: true }]
      : [{ value: '', label: 'No alternate time', selected: true }];

    timeSlots.forEach((slot) => {
      const unavailable = bookedSet.has(slot) || blockedBySelection.has(slot);
      const hour = Number(slot.slice(0, 2));
      options.push({
        value: slot,
        label: unavailable ? `${formatHourLabel(hour)} (Unavailable)` : formatHourLabel(hour),
        disabled: unavailable
      });
    });
    replaceOptions(selectEl, options);
    if (previous) selectEl.value = previous;
    if (selectEl.selectedOptions[0]?.disabled) selectEl.value = '';
  };

  const refreshPreferredTimeDropdown = () => {
    const preferredDate = preferredMeetingDateInput?.value || '';
    const alternateDate = alternateMeetingDateInput?.value || '';
    const alternateSlot = alternateMeetingTimeInput?.value || '';
    const blocked = new Set();
    if (preferredDate && alternateDate && preferredDate === alternateDate && alternateSlot) {
      blocked.add(alternateSlot);
    }
    populateTimeDropdown(preferredMeetingTimeInput, {
      dateValue: preferredDate,
      required: true,
      blockedBySelection: blocked
    });
  };

  const refreshAlternateTimeDropdown = () => {
    const preferredDate = preferredMeetingDateInput?.value || '';
    const alternateDate = alternateMeetingDateInput?.value || '';
    const preferredSlot = preferredMeetingTimeInput?.value || '';
    const blocked = new Set();
    if (preferredDate && alternateDate && preferredDate === alternateDate && preferredSlot) {
      blocked.add(preferredSlot);
    }
    populateTimeDropdown(alternateMeetingTimeInput, {
      dateValue: alternateDate,
      required: false,
      blockedBySelection: blocked
    });
  };

  const syncTimeDropdowns = () => {
    refreshPreferredTimeDropdown();
    refreshAlternateTimeDropdown();
  };

  const loadScheduleAvailability = async () => {
    Object.keys(bookedSlotsByDate).forEach((key) => { delete bookedSlotsByDate[key]; });
    try {
      const response = await jsonFetch('/api/quotes/availability');
      const booked = response?.booked || {};
      Object.entries(booked).forEach(([date, slots]) => {
        bookedSlotsByDate[date] = new Set((Array.isArray(slots) ? slots : []).map(normalizeSlot));
      });
    } catch (err) {
      console.error('load quote availability error:', err);
    }
    syncTimeDropdowns();
  };

  const setFormDisabled = (disabled) => {
    quoteForm.querySelectorAll('input, select, textarea, button').forEach((el) => {
      el.disabled = disabled;
    });
  };

  const updateReferralSourceField = () => {
    const usesExistingMember = String(quoteReferralSourceInput?.value || '') === REFERRAL_SOURCE_EXISTING_MEMBER;
    if (quoteReferralMemberField) {
      quoteReferralMemberField.classList.toggle('is-hidden', !usesExistingMember);
      quoteReferralMemberField.hidden = !usesExistingMember;
    }
    if (quoteReferralMemberNameInput) {
      quoteReferralMemberNameInput.required = usesExistingMember;
      quoteReferralMemberNameInput.disabled = !usesExistingMember;
      if (!usesExistingMember) {
        quoteReferralMemberNameInput.value = '';
      }
    }
  };

  const checkSignedIn = async () => {
    const token = localStorage.getItem('lnf_token');
    if (!token) {
      setSignInStatus();
      setFormDisabled(false);
      return;
    }
    try {
      await jsonFetch('/api/auth/me');
      statusEl.textContent = '';
      setFormDisabled(false);
    } catch (_err) {
      setSignInStatus();
      setFormDisabled(false);
    }
  };

  quoteSpecialtyGrid.addEventListener('click', (event) => {
    const btn = event.target.closest('button[data-option-id]');
    if (!btn) return;
    const specialty = specialtyById.get(btn.dataset.optionId);
    if (!specialty) return;
    openLocationModal(specialty);
  });

  quoteConfirmLocationBtn.addEventListener('click', async () => {
    if (!pendingSpecialty) return;
    const selectedKey = quoteLocationSelect.value;
    const area = locationAreaByKey.get(selectedKey);
    if (!area) {
      quoteLocationStatus.textContent = 'Select a county and state first.';
      return;
    }
    await requestNodeMatch({ county: area.county, state: area.state });
  });

  quoteUseCurrentLocationBtn.addEventListener('click', () => {
    if (!pendingSpecialty) return;
    if (!navigator.geolocation) {
      quoteLocationStatus.textContent = 'Current location is not supported on this device.';
      return;
    }
    quoteLocationStatus.textContent = 'Getting your current location...';
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await requestNodeMatch({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          label: 'Current Location'
        });
      },
      () => {
        quoteLocationStatus.textContent = 'Unable to get your current location. Please choose county and state instead.';
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  });

  changeQuoteSelectionBtn?.addEventListener('click', () => {
    showSelectorPanel();
  });

  closeQuoteLocationModal?.addEventListener('click', closeLocationModal);
  cancelQuoteLocationBtn?.addEventListener('click', closeLocationModal);
  quoteLocationModal?.addEventListener('click', (event) => {
    if (event.target === quoteLocationModal) closeLocationModal();
  });

  contact_pref_input?.addEventListener('change', updateContactMethod);
  quoteReferralSourceInput?.addEventListener('change', updateReferralSourceField);
  updateContactMethod();
  updateReferralSourceField();
  populateDateDropdown(preferredMeetingDateInput, { required: true });
  populateDateDropdown(alternateMeetingDateInput, { required: false });
  syncTimeDropdowns();
  preferredMeetingDateInput?.addEventListener('change', syncTimeDropdowns);
  alternateMeetingDateInput?.addEventListener('change', syncTimeDropdowns);
  preferredMeetingTimeInput?.addEventListener('change', refreshAlternateTimeDropdown);
  alternateMeetingTimeInput?.addEventListener('change', refreshPreferredTimeDropdown);

  renderSpecialtyGrid();
  loadInstallNodes();
  checkSignedIn();

  quoteForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    statusEl.textContent = '';
    window.lnfNotifications?.maybePrompt?.({ reason: 'quote' });
    if (!localStorage.getItem('lnf_token')) {
      setSignInStatus();
      showSignInGate();
      return;
    }

    if (!matchedNode || !selectedSpecialty) {
      statusEl.textContent = 'Choose what your land needs and location first.';
      return;
    }

    const full_name = full_name_input?.value.trim() || '';
    const address = addressInput?.value.trim() || '';
    const contact_pref = contact_pref_input?.value.trim() || '';
    const requiresPhone = contact_pref === 'Phone #';
    const requiresEmail = contact_pref === 'Email';
    const referralSource = quoteReferralSourceInput?.value?.trim() || '';
    const usesExistingMemberReferral = referralSource === REFERRAL_SOURCE_EXISTING_MEMBER;
    const referralMemberName = quoteReferralMemberNameInput?.value?.trim() || '';
    const preferredDate = preferredMeetingDateInput?.value?.trim() || '';
    const preferredTime = preferredMeetingTimeInput?.value?.trim() || '';
    const alternateDate = alternateMeetingDateInput?.value?.trim() || '';
    const alternateTime = alternateMeetingTimeInput?.value?.trim() || '';

    if (!full_name) {
      statusEl.textContent = 'Please enter your name.';
      return;
    }
    if (!address) {
      statusEl.textContent = 'Please enter your property address.';
      return;
    }
    if (!contact_pref) {
      statusEl.textContent = 'Please select a contact method.';
      return;
    }
    if (requiresPhone && !phoneInput?.value.trim()) {
      statusEl.textContent = 'Please add a phone number for phone contact.';
      return;
    }
    if (requiresEmail && !emailInput?.value.trim()) {
      statusEl.textContent = 'Please add an email for email contact.';
      return;
    }
    if (!referralSource) {
      statusEl.textContent = 'Please choose how you found us.';
      return;
    }
    if (usesExistingMemberReferral && !referralMemberName) {
      statusEl.textContent = 'Please enter the existing member name so they receive credit.';
      return;
    }
    if (!preferredDate || !preferredTime) {
      statusEl.textContent = 'Please choose your preferred meeting date and time.';
      return;
    }
    if ((alternateDate && !alternateTime) || (!alternateDate && alternateTime)) {
      statusEl.textContent = 'Choose both alternate date and time, or leave both blank.';
      return;
    }
    if (alternateDate && alternateTime && alternateDate === preferredDate && alternateTime === preferredTime) {
      statusEl.textContent = 'Alternate slot must be different from your preferred slot.';
      return;
    }

    const payload = {
      full_name,
      region: address,
      node_id: matchedNode.id || null,
      contact_pref,
      phone: requiresPhone ? phoneInput.value.trim() : '',
      email: requiresEmail ? emailInput.value.trim() : '',
      referral_source: referralSource,
      referral_member_name: usesExistingMemberReferral ? referralMemberName : '',
      service_types: `${selectedSpecialty.category}: ${selectedSpecialty.label}`,
      service_category: selectedSpecialty.category,
      service_option: selectedSpecialty.label,
      request_county: selectedArea?.county || '',
      request_state: selectedArea?.state || '',
      description: landGoalsInput?.value.trim() || '',
      preferred_meeting_date: preferredDate,
      preferred_meeting_time: preferredTime,
      alternate_meeting_date: alternateDate,
      alternate_meeting_time: alternateTime,
      meeting_schedule_notes: meetingScheduleNotesInput?.value?.trim() || ''
    };

    try {
      await jsonFetch('/api/quotes', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      quoteForm.reset();
      applyMatchedContext();
      updateContactMethod();
      updateReferralSourceField();
      populateDateDropdown(preferredMeetingDateInput, { required: true });
      populateDateDropdown(alternateMeetingDateInput, { required: false });
      await loadScheduleAvailability();
      statusEl.textContent = 'Consultation request received. A member will review your request and follow up soon to confirm your meeting time and next steps.';
    } catch (err) {
      console.error('submit quote error:', err);
      if (String(err.message || '').includes('401')) {
        setSignInStatus();
        setFormDisabled(false);
        showSignInGate();
      } else {
        statusEl.textContent = parseApiError(err);
      }
    }
  });
});
